/**
 * AnnouncementBar - Native CSS scroll-snap implementation
 * 
 * Handles scroll navigation with arrow controls for announcement bar slides.
 * Uses native CSS scroll-snap instead of external libraries like Splide.
 */
class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.slides = [];
    this.currentIndex = 0;
    this.isDesktop = false;
    this.scrollTimeout = null;
    this.isNavigating = false; // Track if we're actively navigating
    
    // Mouse drag properties
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.hasDragged = false;

    // Pagination dots
    this.dots = [];
    this.dotsContainer = null;
  }

  connectedCallback() {
    this.#initSlider();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#unbindEvents();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  /**
   * Initialize the slider by finding DOM elements and setting up initial state
   */
  #initSlider() {
    try {
      const config = this.#getConfig();
      
      this.slider = this.querySelector('.announcement-bar__slider');
      this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
      this.prevArrowWrapper = this.querySelector('.announcement-bar__arrow-wrapper--prev');
      this.nextArrowWrapper = this.querySelector('.announcement-bar__arrow-wrapper--next');
      this.prevArrow = this.querySelector('.announcement-bar__arrow--prev');
      this.nextArrow = this.querySelector('.announcement-bar__arrow--next');
      
      if (!this.slider || this.slides.length === 0) {
        console.warn('Announcement bar: No slider or slides found');
        return;
      }

      // Set initial state
      this.#updateViewport();
      this.currentIndex = 0; // Start at first slide
      
      // Setup arrow functionality for 3+ items
      if (config.itemCount >= 3) {
        this.#setupArrowNavigation();
      } else {
        this.#hideArrows();
      }

      this.#createDots();

      // Set initial states after a brief delay to ensure DOM is ready
      setTimeout(() => {
        this.#updateArrowVisibility();
        this.#updateDots();
      }, 100);
    } catch (error) {
      console.error('Failed to initialize announcement bar:', error);
    }
  }

  /**
   * Get configuration data from the data attribute
   */
  #getConfig() {
    try {
      const configData = this.dataset.config;
      return configData ? JSON.parse(configData) : { itemCount: 0 };
    } catch (error) {
      console.error('Failed to parse announcement bar config:', error);
      return { itemCount: 0 };
    }
  }

  /**
   * Bind event listeners
   */
  #bindEvents() {
    // Listen for scroll events to update arrow visibility
    if (this.slider) {
      this.slider.addEventListener('scroll', this.#onScroll.bind(this));
      
      // Add mouse drag support for desktop - only mousedown on slider
      this.slider.addEventListener('mousedown', this.#onMouseDown.bind(this));
    }

    // Listen for resize events to handle responsive behavior
    window.addEventListener('resize', this.#onResize.bind(this));
  }

  /**
   * Unbind event listeners
   */
  #unbindEvents() {
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.#onScroll.bind(this));
      this.slider.removeEventListener('mousedown', this.#onMouseDown.bind(this));
    }
    window.removeEventListener('resize', this.#onResize.bind(this));
    
    // Clean up any global drag listeners
    this.#removeDragListeners();
  }

  /**
   * Handle scroll events to update current slide index and arrow visibility
   */
  #onScroll() {
    if (!this.slider || this.slides.length === 0) return;

    // Don't interfere with programmatic navigation
    if (this.isNavigating) return;

    // For immediate feedback during manual scrolling, check position right away
    const { isAtStart, isAtEnd } = this.#getScrollPosition();
    
    // Only update arrows immediately if we've reached start/end during manual scroll
    if (isAtStart || isAtEnd) {
      this.#calculateCurrentIndex();
      this.#updateArrowVisibility();
      this.#updateDots();
    }

    // Use a small delay for other position updates to avoid too frequent updates
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      // Double-check we're not navigating before updating
      if (!this.isNavigating) {
        this.#calculateCurrentIndex();
        this.#updateArrowVisibility();
        this.#updateDots();
      }
    }, 50);
  }

  /**
   * Calculate current slide index based on scroll position
   */
  #calculateCurrentIndex() {
    if (!this.slider || this.slides.length === 0) return;

    const scrollLeft = this.slider.scrollLeft;
    const slideWidth = this.slides[0].offsetWidth;
    const gap = parseInt(getComputedStyle(this.slider).gap) || 20;
    const slideWithGap = slideWidth + gap;
    
    // Calculate index more precisely considering gaps
    const newIndex = Math.round(scrollLeft / slideWithGap);
    this.currentIndex = Math.max(0, Math.min(newIndex, this.slides.length - 1));
  }

  /**
   * Handle window resize events
   */
  #onResize() {
    this.#updateViewport();
    this.#updateArrowVisibility();
    this.#updateDots();
  }

  /**
   * Update viewport-specific settings
   */
  #updateViewport() {
    this.isDesktop = window.innerWidth >= 990;
  }

  /**
   * Setup arrow navigation for desktop
   */
  #setupArrowNavigation() {
    if (!this.prevArrow || !this.nextArrow) return;

    // Previous arrow click handler
    this.prevArrow.addEventListener('click', (e) => {
      e.preventDefault();
      this.#navigatePrev();
    });

    // Next arrow click handler
    this.nextArrow.addEventListener('click', (e) => {
      e.preventDefault();
      this.#navigateNext();
    });
  }

  /**
   * Navigate to previous slide
   */
  #navigatePrev() {
    if (!this.slider || this.currentIndex <= 0) return;

    const targetIndex = Math.max(0, this.currentIndex - this.#getSlidesPerPage());
    
    // Set navigation flag to prevent scroll handler interference
    this.isNavigating = true;
    
    // Immediately update arrows for the target position
    this.#updateArrowsForTargetPosition(targetIndex);
    
    this.#scrollToSlide(targetIndex);
    
    // Clear navigation flag after scroll completes
    setTimeout(() => {
      this.isNavigating = false;
      this.currentIndex = targetIndex;
      this.#updateDots();
    }, 350); // Slightly longer than CSS transition
  }

  /**
   * Navigate to next slide
   */
  #navigateNext() {
    if (!this.slider || this.slides.length === 0) return;

    const slidesPerPage = this.#getSlidesPerPage();
    const maxIndex = Math.max(0, this.slides.length - slidesPerPage);
    const targetIndex = Math.min(maxIndex, this.currentIndex + slidesPerPage);
    
    // Set navigation flag to prevent scroll handler interference
    this.isNavigating = true;
    
    // Immediately update arrows for the target position
    this.#updateArrowsForTargetPosition(targetIndex);
    
    this.#scrollToSlide(targetIndex);
    
    // Clear navigation flag after scroll completes
    setTimeout(() => {
      this.isNavigating = false;
      this.currentIndex = targetIndex;
      this.#updateDots();
    }, 350); // Slightly longer than CSS transition
  }

  /**
   * Scroll to a specific slide index
   */
  #scrollToSlide(index) {
    if (!this.slider || !this.slides[index]) return;

    const slideWidth = this.slides[0].offsetWidth;
    const gap = parseInt(getComputedStyle(this.slider).gap) || 20;
    const scrollPosition = index * (slideWidth + gap);

    this.slider.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }

  /**
   * Get number of slides visible per page based on viewport
   */
  #getSlidesPerPage() {
    const config = this.#getConfig();
    
    if (this.isDesktop) {
      // Desktop: 1 slide for single item, 2 slides for 2+ items
      return config.itemCount === 1 ? 1 : 2;
    } else {
      // Mobile: Always 1 slide at a time
      return 1;
    }
  }

  /**
   * Update arrow visibility based on current position and viewport
   */
  #updateArrowVisibility() {
    if (!this.prevArrowWrapper || !this.nextArrowWrapper) return;

    // Hide arrows on mobile
    if (!this.isDesktop) {
      this.prevArrowWrapper.style.display = 'none';
      this.nextArrowWrapper.style.display = 'none';
      return;
    }

    // Show arrows on desktop for 3+ items (handled by CSS)
    const config = this.#getConfig();
    if (config.itemCount < 3) {
      this.#hideArrows();
      return;
    }

    // Calculate if we're at start or end positions
    const { isAtStart, isAtEnd } = this.#getScrollPosition();

    // Update previous arrow wrapper with CSS classes for smooth transitions
    this.#updateArrowState(this.prevArrowWrapper, !isAtStart);
    
    // Update next arrow wrapper with CSS classes for smooth transitions
    this.#updateArrowState(this.nextArrowWrapper, !isAtEnd);
  }

  /**
   * Update arrows immediately based on target position (for proactive transitions)
   */
  #updateArrowsForTargetPosition(targetIndex) {
    if (!this.prevArrowWrapper || !this.nextArrowWrapper) return;

    // Hide arrows on mobile
    if (!this.isDesktop) return;

    // Show arrows on desktop for 3+ items (handled by CSS)
    const config = this.#getConfig();
    if (config.itemCount < 3) return;

    // Calculate if target position will be at start or end
    const slidesPerPage = this.#getSlidesPerPage();
    const maxIndex = Math.max(0, this.slides.length - slidesPerPage);
    
    const willBeAtStart = targetIndex === 0;
    const willBeAtEnd = targetIndex >= maxIndex;


    // Force immediate arrow state update - bypass any delays
    if (willBeAtStart) {
      this.prevArrowWrapper.classList.remove('announcement-bar__arrow-wrapper--visible');
      this.prevArrowWrapper.classList.add('announcement-bar__arrow-wrapper--hidden');
    } else {
      this.prevArrowWrapper.classList.remove('announcement-bar__arrow-wrapper--hidden');
      this.prevArrowWrapper.classList.add('announcement-bar__arrow-wrapper--visible');
    }

    if (willBeAtEnd) {
      this.nextArrowWrapper.classList.remove('announcement-bar__arrow-wrapper--visible');
      this.nextArrowWrapper.classList.add('announcement-bar__arrow-wrapper--hidden');
    } else {
      this.nextArrowWrapper.classList.remove('announcement-bar__arrow-wrapper--hidden');
      this.nextArrowWrapper.classList.add('announcement-bar__arrow-wrapper--visible');
    }
  }

  /**
   * Get current scroll position relative to start and end
   */
  #getScrollPosition() {
    if (!this.slider || this.slides.length === 0) {
      return { isAtStart: true, isAtEnd: true };
    }

    const scrollLeft = this.slider.scrollLeft;
    const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
    
    // Consider a small threshold for floating point precision
    const threshold = 5;
    const isAtStart = scrollLeft <= threshold;
    const isAtEnd = scrollLeft >= maxScrollLeft - threshold;

    return { isAtStart, isAtEnd };
  }

  /**
   * Update arrow wrapper state using CSS classes for smooth transitions
   */
  #updateArrowState(arrowWrapper, shouldBeVisible) {
    if (!arrowWrapper) return;

    // Remove existing state classes
    arrowWrapper.classList.remove('announcement-bar__arrow-wrapper--visible', 'announcement-bar__arrow-wrapper--hidden');
    
    // Add appropriate state class
    if (shouldBeVisible) {
      arrowWrapper.classList.add('announcement-bar__arrow-wrapper--visible');
    } else {
      arrowWrapper.classList.add('announcement-bar__arrow-wrapper--hidden');
    }
  }

  /**
   * Create pagination dots
   */
  #createDots() {
    if (this.slides.length <= 1) return;

    this.dotsContainer = document.createElement('div');
    this.dotsContainer.className = 'announcement-bar__dots';

    for (let i = 0; i < this.slides.length; i++) {
      const dot = document.createElement('button');
      dot.className = 'announcement-bar__dot';
      dot.setAttribute('type', 'button');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.addEventListener('click', () => this.#goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }

    this.appendChild(this.dotsContainer);
    this.dots = Array.from(this.dotsContainer.querySelectorAll('.announcement-bar__dot'));
  }

  /**
   * Update active state of pagination dots based on current index and viewport
   */
  #updateDots() {
    if (!this.dotsContainer || !this.dots || this.dots.length === 0) return;

    const config = this.#getConfig();

    // Hide if toggled off via settings
    if (this.isDesktop && !config.showDotsDesktop) {
      this.dotsContainer.style.display = 'none';
      return;
    }
    if (!this.isDesktop && !config.showDotsMobile) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    // On desktop with 2 or fewer items, all slides are always visible — hide dots
    if (this.isDesktop && config.itemCount <= 2) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    this.dotsContainer.style.display = '';

    const slidesPerPage = this.#getSlidesPerPage();
    this.dots.forEach((dot, i) => {
      const isActive = i >= this.currentIndex && i < this.currentIndex + slidesPerPage;
      dot.classList.toggle('announcement-bar__dot--active', isActive);
    });
  }

  /**
   * Navigate to a specific slide index (used by dot clicks)
   */
  #goToSlide(index) {
    if (!this.slider) return;

    const slidesPerPage = this.#getSlidesPerPage();
    const maxIndex = Math.max(0, this.slides.length - slidesPerPage);
    const targetIndex = Math.min(Math.max(0, index), maxIndex);

    this.isNavigating = true;
    this.#updateArrowsForTargetPosition(targetIndex);
    this.#scrollToSlide(targetIndex);

    setTimeout(() => {
      this.isNavigating = false;
      this.currentIndex = targetIndex;
      this.#updateArrowVisibility();
      this.#updateDots();
    }, 350);
  }

  /**
   * Hide arrows completely
   */
  #hideArrows() {
    if (this.prevArrowWrapper) {
      this.prevArrowWrapper.style.display = 'none';
    }
    if (this.nextArrowWrapper) {
      this.nextArrowWrapper.style.display = 'none';
    }
  }

  /**
   * Add global drag listeners
   */
  #addDragListeners() {
    // Bind methods to preserve 'this' context
    this.boundMouseMove = this.#onMouseMove.bind(this);
    this.boundMouseUp = this.#onMouseUp.bind(this);
    
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
    document.addEventListener('mouseleave', this.boundMouseUp); // Handle mouse leaving document
  }

  /**
   * Remove global drag listeners
   */
  #removeDragListeners() {
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = null;
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
      document.removeEventListener('mouseleave', this.boundMouseUp);
      this.boundMouseUp = null;
    }
  }

  /**
   * Handle mouse down event for drag start
   */
  #onMouseDown(e) {
    // Only enable drag on desktop and for left mouse button
    if (!this.isDesktop || e.button !== 0) return;
    
    // Don't start drag on interactive elements
    if (e.target.closest('a, button')) return;
    
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = e.clientX; // Use clientX for better consistency
    this.scrollLeft = this.slider.scrollLeft;
    
    // Add global listeners to track mouse anywhere
    this.#addDragListeners();
    
    // Disable text selection during drag
    document.body.style.userSelect = 'none';
    this.slider.style.cursor = 'grabbing';
    
    // Prevent default to avoid text selection
    e.preventDefault();
  }

  /**
   * Handle mouse move event for dragging
   */
  #onMouseMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    
    const x = e.clientX;
    const walk = (x - this.startX) * 3; // Higher multiplier for quicker movement
    const newScrollLeft = this.scrollLeft - walk;
    
    // Apply scroll with bounds checking
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    this.slider.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
    
    // Track that we've actually dragged (for click prevention)
    if (Math.abs(walk) > 2) { // Even lower threshold for quicker response
      this.hasDragged = true;
    }
  }

  /**
   * Handle mouse up/leave events for drag end
   */
  #onMouseUp(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    // Remove global listeners
    this.#removeDragListeners();
    
    // Re-enable text selection
    document.body.style.userSelect = '';
    this.slider.style.cursor = '';
    
    // If we dragged significantly, prevent click events on children
    if (this.hasDragged) {
      // Add a brief delay to prevent click events from firing
      setTimeout(() => {
        this.hasDragged = false;
      }, 50); // Slightly longer delay for better click prevention
    }
  }
}

// Register the custom element
if (!customElements.get("announcement-bar")) {
  customElements.define("announcement-bar", AnnouncementBar);
}